  import { preciseDateCache } from './precise-date-cache';
  import type { AxiosResponse } from 'axios';

  // Re-export preciseDateCache for external use
  export { preciseDateCache };

  // Symbol to store original ISO string on Date objects
  const ORIGINAL_ISO_STRING = Symbol('originalIsoString');

  // Helper to attach original ISO string to Date object
  export function createDateWithOriginal(dateObj: Date, originalString: string): Date {
    // Store the original string as a non-enumerable property
    Object.defineProperty(dateObj, ORIGINAL_ISO_STRING, {
      value: originalString,
      writable: false,
      enumerable: false,
      configurable: false
    });
    return dateObj;
  }

  // Helper to get original ISO string from Date object
  export function getOriginalIsoString(date: Date): string | null {
    return (date as any)[ORIGINAL_ISO_STRING] || null;
  }

  // DIG-layer event states — used to determine eventLayer for cache entries.
  // Duplicated here to avoid importing from machines/ (shared/ must not depend on feature modules).
  const DIG_LAYER_STATES = [
    'dig', 'dump', 'swing-empty', 'swing-loaded',
    'dump-no-truck', 'boulder-handling', 'cycle-state-unknown',
  ];

  function deriveEventLayer(state: string | undefined): string | undefined {
    if (!state) return undefined;
    const s = state.toLowerCase();
    if (DIG_LAYER_STATES.includes(s)) return 'dig';
    if (s.startsWith('truck') || s === 'unproductive') return 'truck';
    return undefined;
  }

  function convertDateTimeStringsToDateInternal(
    obj: any,
    shouldCachePrecise: boolean,
    cacheEntries?: Array<[number, string, string?]>,
    parentEventLayer?: string
  ): any {
    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => convertDateTimeStringsToDateInternal(item, shouldCachePrecise, cacheEntries, parentEventLayer));
    }

    // Handle objects
    if (obj !== null && typeof obj === 'object') {
      const newObj: { [key: string]: any } = { ...obj };

      // Detect event layer from `state` field on this object (e.g., MachineEvent)
      const eventLayer = deriveEventLayer(newObj.state) ?? parentEventLayer;

      for (const key in newObj) {
        if (Object.prototype.hasOwnProperty.call(newObj, key)) {
          newObj[key] = convertDateTimeStringsToDateInternal(newObj[key], shouldCachePrecise, cacheEntries, eventLayer);
        }
      }
      return newObj;
    }

    // Handle strings
    if (typeof obj === 'string') {
      const iso8601 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?Z$/;
      if (iso8601.test(obj)) {
        try {
          // Store the original string BEFORE parsing
          const originalString = obj;

          // Parse with milliseconds preserved
          const parts = obj.match(
            /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?Z$/
          );

          if (parts) {
            const [, y, m, d, h, min, s, ms] = parts;
            const milliseconds = ms ? parseFloat('0' + ms) * 1000 : 0;
            // The `Z` suffix marks the string as UTC — parse it as UTC so the
            // resulting Date is the true instant (previously it was fed to the
            // local-time constructor, shifting every timestamp by the user's
            // UTC offset).
            const date = new Date(Date.UTC(
              parseInt(y),
              parseInt(m) - 1,
              parseInt(d),
              parseInt(h),
              parseInt(min),
              parseInt(s),
              // Clamp so .999999 (≥999.5ms) can't round into the next second
              Math.min(999, Math.round(milliseconds))
            ));
            // Date.UTC silently rolls over out-of-range components
            // (month 13 → January next year). Verify the result matches
            // what we parsed so invalid strings become null instead of a
            // wrong date.
            if (
              isNaN(date.getTime()) ||
              date.getUTCFullYear() !== parseInt(y) ||
              date.getUTCMonth() !== parseInt(m) - 1 ||
              date.getUTCDate() !== parseInt(d) ||
              date.getUTCHours() !== parseInt(h) ||
              date.getUTCMinutes() !== parseInt(min) ||
              date.getUTCSeconds() !== parseInt(s)
            ) {
              throw new RangeError('Invalid time value');
            }

            // Collect cache entry if requested (batch operation)
            if (shouldCachePrecise && cacheEntries) {
              cacheEntries.push([date.getTime(), originalString, parentEventLayer]);
            }

            // Attach original string to the Date object
            return createDateWithOriginal(date, originalString);
          } else {
            // Fallback to native Date parsing (preserves milliseconds)
            const date = new Date(obj);
            if (isNaN(date.getTime())) {
              throw new RangeError('Invalid time value');
            }

            // Collect cache entry if requested (batch operation)
            if (shouldCachePrecise && cacheEntries) {
              cacheEntries.push([date.getTime(), originalString, parentEventLayer]);
            }

            // Attach original string to the Date object
            return createDateWithOriginal(date, originalString);
          }
        } catch {
          return null;
        }
      }
      // Return unchanged for non-ISO strings
      return obj;
    }

    // Return unchanged for non-strings/non-objects
    return obj;
  }

  function convertDateTimeStringsToDate(response: AxiosResponse): AxiosResponse {
    const shouldCachePrecise = response.config?.preciseDate === true;

    // Collect all cache entries during traversal, then batch insert
    if (shouldCachePrecise) {
      const cacheEntries: Array<[number, string, string?]> = [];
      response.data = convertDateTimeStringsToDateInternal(response.data, shouldCachePrecise, cacheEntries);

      // Batch insert all collected entries at once (single localStorage write)
      if (cacheEntries.length > 0) {
        preciseDateCache.setBatch(cacheEntries);
      }
    } else {
      response.data = convertDateTimeStringsToDateInternal(response.data, shouldCachePrecise);
    }

    return response;
  }

  export default convertDateTimeStringsToDate;
